/**
 * JSON-LD for the /region/ and /kategorie/ landing pages.
 *
 * Pure functions so the graph can be unit-tested without rendering anything.
 * They emit the same shapes the static pages emitted, because losing structured
 * data would be a silent SEO regression in a change that is supposed to be
 * invisible to search engines.
 */

const SITE = 'https://toastably.com';

// Page.js already appends an Organization node via organizationSchema(), keyed
// `${marketplaceRootURL}#organization` - no slash before the fragment. Emitting
// our own with `${SITE}/#organization` produced TWO Organizations with
// different IRIs, so Service.provider resolved to a bare stub while the node
// carrying logo/sameAs/description was a separate entity. Reference the one
// Page emits instead of adding a second.
const ORGANIZATION_ID = `${SITE}#organization`;

/**
 * Breadcrumbs from an ordered list of `{ name, path }`. The last crumb is the
 * current page and carries no `item`, matching what the static pages emitted.
 *
 * @param {Array<Object>} crumbs
 * @returns {Object} BreadcrumbList node
 */
const breadcrumbs = crumbs => ({
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    ...(crumb.path ? { item: `${SITE}${crumb.path}` } : {}),
  })),
});

const faqPage = items =>
  items.length > 0
    ? {
        '@type': 'FAQPage',
        mainEntity: items.map(item => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      }
    : null;

/**
 * The graph for one canton page.
 *
 * `areaServed` is an AdministrativeArea rather than a Country: the page is
 * about one canton. Same shape `areaServedSchema` emits for a listing.
 *
 * No AggregateOffer. The category pages carry real, researched price ranges;
 * per canton those numbers would be invented, and invented structured data is a
 * manual-action risk.
 *
 * @param {Object} canton `{ option, label }`
 * @param {Array} faqItems `{ q, a }`
 * @returns {Array} JSON-LD nodes
 */
export const regionPageSchema = (canton, faqItems = []) =>
  [
    breadcrumbs([
      { name: 'Startseite', path: '/' },
      { name: 'Regionen', path: '/region/' },
      { name: canton.label },
    ]),
    {
      '@type': 'Service',
      name: `Hochzeitsdienstleistungen in ${canton.label}`,
      serviceType: 'Hochzeitsdienstleistungen',
      provider: { '@id': ORGANIZATION_ID },
      areaServed: { '@type': 'AdministrativeArea', name: canton.label },
    },
    faqPage(faqItems),
  ].filter(Boolean);

/**
 * The graph for one category page.
 *
 * Keeps the AggregateOffer the static pages carried: those price ranges are
 * real and researched, and dropping them would lose a rich-result signal.
 *
 * @param {Object} category entry from categories.json
 * @returns {Array} JSON-LD nodes
 */
export const categoryPageSchema = category => {
  const { schema = {}, faq = {} } = category;
  const offers =
    schema.priceLow && schema.priceHigh
      ? {
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: schema.priceCurrency || 'CHF',
            lowPrice: String(schema.priceLow),
            highPrice: String(schema.priceHigh),
          },
        }
      : {};

  return [
    breadcrumbs([
      { name: 'Startseite', path: '/' },
      { name: 'Kategorien', path: '/kategorie/' },
      { name: category.shortLabel || schema.serviceName },
    ]),
    {
      '@type': 'Service',
      name: schema.serviceName,
      serviceType: schema.serviceType,
      provider: { '@id': ORGANIZATION_ID },
      areaServed: { '@type': 'Country', name: 'Schweiz' },
      ...offers,
    },
    faqPage(faq.items || []),
  ].filter(Boolean);
};

/**
 * The graph for one category-in-canton page. No AggregateOffer, for the same
 * reason as the canton pages: a per-canton price range would be invented.
 *
 * @param {Object} category entry from categories.json plus `slug` and `shortLabel`
 * @param {Object} canton `{ option, label }`
 * @param {Array} faqItems `{ q, a }`
 * @returns {Array} JSON-LD nodes
 */
export const categoryRegionPageSchema = (category, canton, faqItems = []) => {
  const { schema = {} } = category;
  return [
    breadcrumbs([
      { name: 'Startseite', path: '/' },
      { name: 'Kategorien', path: '/kategorie/' },
      { name: category.shortLabel, path: `/kategorie/${category.slug}/` },
      { name: canton.label },
    ]),
    {
      '@type': 'Service',
      name: `${schema.serviceName} im Kanton ${canton.label}`,
      serviceType: schema.serviceType,
      provider: { '@id': ORGANIZATION_ID },
      areaServed: { '@type': 'AdministrativeArea', name: canton.label },
    },
    faqPage(faqItems),
  ].filter(Boolean);
};

/**
 * The graph for a hub page listing its children.
 *
 * @param {string} name hub name, e.g. 'Regionen'
 * @param {string} path hub path, e.g. '/region/'
 * @param {Array} items `{ name, path }`
 * @returns {Array} JSON-LD nodes
 */
export const hubPageSchema = (name, path, items) => [
  breadcrumbs([{ name: 'Startseite', path: '/' }, { name }]),
  {
    '@type': 'ItemList',
    name,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE}${item.path}`,
    })),
  },
];
